// ---------------------------------------------------------------------------
// Loads a complete EvalHid project from a local folder (File System Access
// API). No persistence: the result lives only in memory for the duration of
// the session (see CLAUDE.md, "About persistence").
//
// A real project's structure (confirmed against ModeloTuria):
//   MyProject.ehd              <- Access/Jet DB, found by extension
//   DirTrabajo1/                <- real folder name, taken from
//                                 APO_ARCHIVOS_SIMULACION.RUTA_ESCENARIO
//     Ent_Precip.csv, Ent_Etp.csv, Ent_Tempe.csv, Ent_Bomb.csv, ...
//
// APO_ARCHIVOS_SIMULACION gives the real name of each input file by LABEL
// (e.g. ARCHIVO_PRECIPITACION -> "Ent_Precip.csv"): fixed file names can't be
// assumed, each project can name them differently.
// ---------------------------------------------------------------------------
import { loadEhd } from "./ehd.ts";
import { parseDailySeriesAsync } from "./csvWorkerClient.ts";
import { resolveIndicesByName, type DailySeries } from "./dailySeries.ts";
import { detectScale } from "./seriesScale.ts";
import { exceedsMaxImportSize } from "./fileSizeLimits.ts";
import type { ReadableFile, ReadableFolder } from "./fsReadable.ts";
import { lastSegment } from "./paths.ts";
import type { EvalHidProject } from "./types.ts";
import { TranslatableError, type Translate } from "../i18n/translate.ts";
import type { Language } from "./language.ts";

const LABEL_PRECIPITATION = "ARCHIVO_PRECIPITACION";
const LABEL_ETP = "ARCHIVO_ETP";
// Sic: the pumping LABEL is spelled correctly, but "ARCHIVO_ETIQUETAS"
// has a real typo in the original DB ("ARVHIVO_ETIQUETAS") — doesn't affect
// the input series, noted here in case that table gets used later.
const LABEL_TEMPERATURE = "ARCHIVO_TEMPERATURA";
const LABEL_PUMPING = "ARCHIVO_BOMBEOS";
const LABEL_SCENARIO_PATH = "RUTA_ESCENARIO";

export interface LoadedProject {
  project: EvalHidProject;
  /** Input series read successfully, by logical label. */
  series: {
    precipitation: DailySeries | null;
    etp: DailySeries | null;
    temperature: DailySeries | null;
    pumping: DailySeries | null;
  };
  /** Non-blocking notices (missing optional files, folder not found...). */
  notices: string[];
}

/** Exported for importCsvProject.ts's own reads (precip/etp/temp/pumping series, same folder-reading primitive). */
export async function readArrayBuffer(dir: ReadableFolder, fileName: string): Promise<ArrayBuffer> {
  const handle = await dir.getFileHandle(fileName);
  const file = await handle.getFile();
  return file.arrayBuffer();
}

/** A .ehd found while searching the chosen folder (and its subfolders) — see listEhdFiles. */
export interface FoundEhd {
  /** Full relative path from the chosen folder, with "/" — to display it when there's a choice among several. */
  path: string;
  /**
   * Folder that actually contains the .ehd — not necessarily the one chosen
   * by the user, if it was in a subfolder. This is the one to pass to
   * openProject()/loadSubBasinLayer(), never the chosen folder as-is:
   * RUTA_ESCENARIO and the GIS shapefile are paths relative to the .ehd
   * itself, not to wherever the user opened the picker.
   */
  folder: ReadableFolder;
  fileName: string;
  /** Name to display as "project name": its own subfolder's name if it was nested, or the chosen folder's name if it was at the top level. */
  projectName: string;
}

/** Below this, a folder with many levels of subfolders (or a symlink loop) stops being explored — real EvalHid projects are flat (see file header). */
const MAX_EHD_SEARCH_DEPTH = 5;

async function findEhdFiles(
  folder: ReadableFolder,
  pathPrefix: string,
  rootFolderName: string,
  remainingDepth: number
): Promise<FoundEhd[]> {
  const found: FoundEhd[] = [];
  for await (const [name, handle] of folder.entries()) {
    if (handle.kind === "file" && name.toLowerCase().endsWith(".ehd")) {
      found.push({
        path: pathPrefix + name,
        folder,
        fileName: name,
        projectName: pathPrefix ? (lastSegment(pathPrefix) ?? rootFolderName) : rootFolderName,
      });
    } else if (handle.kind === "directory" && remainingDepth > 0) {
      found.push(
        ...(await findEhdFiles(handle, `${pathPrefix}${name}/`, rootFolderName, remainingDepth - 1))
      );
    }
  }
  return found;
}

/**
 * All the .ehd files in the chosen folder, also searching its subfolders —
 * there can be more than one (several saved projects together, each in its
 * own). `rootFolderName` is the name to display for the ones at the top
 * level (ones in a subfolder use that subfolder's name instead, see
 * FoundEhd.projectName).
 */
export async function listEhdFiles(
  root: ReadableFolder,
  rootFolderName: string
): Promise<FoundEhd[]> {
  return findEhdFiles(root, "", rootFolderName, MAX_EHD_SEARCH_DEPTH);
}

/**
 * Links `entities` with `series` by name — but only the ones that don't
 * already have a real index (`indexOf(e) === 0`), so as not to overwrite one
 * already persisted (v1.0 project, see ehd.ts/groupZonesV1). Idempotent:
 * can be called every time the project is opened without over-resolving.
 */
function resolveIfUnlinked<T>(
  entities: T[],
  series: DailySeries | null,
  indexOf: (entity: T) => number,
  nameOf: (entity: T) => string,
  withIndex: (entity: T, index: number) => T
): T[] {
  if (!series) return entities;
  return resolveIndicesByName(entities, series, nameOf, (e, index) =>
    indexOf(e) === 0 ? withIndex(e, index) : e
  );
}

async function findEhdFile(root: ReadableFolder, chosenName?: string): Promise<ReadableFile> {
  if (chosenName) return root.getFileHandle(chosenName);
  for await (const [name, handle] of root.entries()) {
    if (handle.kind === "file" && name.toLowerCase().endsWith(".ehd")) {
      return handle;
    }
  }
  throw new TranslatableError("project.noEhdFoundError");
}

/**
 * Loads a complete EvalHid project from the root folder chosen by the user
 * (or an example, see examples.ts). `ehdName` is only needed when the folder
 * has more than one .ehd (see listEhdFiles/App.tsx, which asks the user
 * which one to open before getting here) — with just one, it's used
 * directly without needing to specify it.
 */
export async function openProject(
  t: Translate,
  language: Language,
  root: ReadableFolder,
  ehdName?: string
): Promise<LoadedProject> {
  const notices: string[] = [];

  const ehdHandle = await findEhdFile(root, ehdName);
  const ehdFile = await ehdHandle.getFile();
  if (exceedsMaxImportSize(ehdFile)) {
    throw new TranslatableError("project.ehdTooLarge", { name: ehdFile.name });
  }
  const project = loadEhd(await ehdFile.arrayBuffer(), t);

  const files = new Map(project.simulationFiles.map((a) => [a.label, a.path]));

  const scenarioPath = files.get(LABEL_SCENARIO_PATH);
  const scenarioFolderName = scenarioPath ? lastSegment(scenarioPath) : null;

  let scenarioFolder: ReadableFolder | null = null;
  if (scenarioFolderName) {
    try {
      scenarioFolder = await root.getDirectoryHandle(scenarioFolderName);
    } catch {
      notices.push(t("project.scenarioFolderNotFoundWarning", { folder: scenarioFolderName }));
    }
  } else {
    notices.push(t("project.noScenarioPathWarning"));
  }

  async function loadSeries(label: string, required: boolean): Promise<DailySeries | null> {
    const fileName = files.get(label);
    if (!fileName) {
      if (required) notices.push(t("project.fileNotDeclaredWarning", { label }));
      return null;
    }
    if (!scenarioFolder) return null;
    try {
      const bytes = await readArrayBuffer(scenarioFolder, fileName);
      return await parseDailySeriesAsync(bytes, language);
    } catch {
      const key = required
        ? "project.requiredSeriesNotReadWarning"
        : "project.optionalSeriesNotReadWarning";
      notices.push(t(key, { file: fileName, label }));
      return null;
    }
  }

  const [precipitation, etp, temperature, pumping] = await Promise.all([
    loadSeries(LABEL_PRECIPITATION, true),
    loadSeries(LABEL_ETP, true),
    loadSeries(LABEL_TEMPERATURE, false),
    loadSeries(LABEL_PUMPING, false),
  ]);

  // The column name is only used HERE, once, to link each series with its
  // sub-basin/aquifer — from this point on, every read (engine, validations,
  // UI...) always goes through the already-persisted index.
  let subBasins = resolveIfUnlinked(
    project.subBasins,
    precipitation,
    (c) => c.precipCol,
    (c) => c.description,
    (c, index) => ({ ...c, precipCol: index })
  );
  subBasins = resolveIfUnlinked(
    subBasins,
    etp,
    (c) => c.etpCol,
    (c) => c.description,
    (c, index) => ({ ...c, etpCol: index })
  );
  subBasins = resolveIfUnlinked(
    subBasins,
    temperature,
    (c) => c.tempCol,
    (c) => c.description,
    (c, index) => ({ ...c, tempCol: index })
  );
  // Resolved by SUB-BASIN name, independent of the aquifer resolution right
  // below (same shared file/series, see SubBasin.pumpingCol's own doc
  // comment) — a sub-basin doesn't inherit its aquifer's column.
  subBasins = resolveIfUnlinked(
    subBasins,
    pumping,
    (c) => c.pumpingCol,
    (c) => c.description,
    (c, index) => ({ ...c, pumpingCol: index })
  );
  const aquifers = resolveIfUnlinked(
    project.aquifers,
    pumping,
    (a) => a.pumpingCol,
    (a) => a.name,
    (a, index) => ({ ...a, pumpingCol: index })
  );

  // loadEhd defaults to "daily" (the real .ehd has no Escala_Temporal
  // equivalent to read — see its own comment) — corrected here from the
  // real precipitation dates, once they're actually loaded.
  const seriesScale = precipitation ? detectScale(precipitation.dates) : project.seriesScale;

  return {
    project: { ...project, subBasins, aquifers, seriesScale },
    series: { precipitation, etp, temperature, pumping },
    notices,
  };
}
